import { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import axios from "axios";
import { API } from "../config";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const getStoredToken = () => sessionStorage.getItem("token");
const setStoredToken = (t) => sessionStorage.setItem("token", t);
const removeStoredToken = () => sessionStorage.removeItem("token");

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!cancelled) setUser(response.data);
        } catch {
          removeStoredToken();
          if (!cancelled) setToken(null);
        }
      }
      if (!cancelled) setLoading(false);
    };
    checkAuth();
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setStoredToken(response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data.user;
  }, []);

  const register = useCallback(async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    setStoredToken(response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data.user;
  }, []);

  const logout = useCallback(() => {
    removeStoredToken();
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch { /* silent */ }
    }
  }, [token]);

  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, authAxios, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
